import React from 'react';
import { Clock } from 'lucide-react';

interface OperationalTimesCardProps {
  request: {
    id: string;
    createdAt: string;
    process_stage?: string;
    timestamp_open?: string | null;
    timestamp_false_alarm?: string | null;
    timestamp_in_progress?: string | null;
    timestamp_on_site?: string | null;
    timestamp_in_analyzes?: string | null;
    timestamp_decommission?: string | null;
    timestamp_repair_begin?: string | null;
    timestamp_waiting_parts?: string | null;
    timestamp_repair_done?: string | null;
    timestamp_recommission?: string | null;
    timestamp_recorded?: string | null;
    timestamp_done?: string | null;
  };
}

interface TimelineStage {
  key: keyof OperationalTimesCardProps['request'];
  label: string;
  description: string;
}

const TIMELINE_STAGES: TimelineStage[] = [
  { key: 'timestamp_open', label: 'Reported', description: 'Ticket opened' },
  { key: 'timestamp_false_alarm', label: 'False Alarm', description: 'Marked as false alarm' },
  { key: 'timestamp_in_progress', label: 'In Progress', description: 'Work started' },
  { key: 'timestamp_on_site', label: 'On Site', description: 'Team arrived at location' },
  { key: 'timestamp_in_analyzes', label: 'In Analyzes', description: 'Analysis phase' },
  { key: 'timestamp_decommission', label: 'Decommissioning', description: 'Shutdown initiated' },
  { key: 'timestamp_repair_begin', label: 'Repair Begin', description: 'Repair work started' },
  { key: 'timestamp_waiting_parts', label: 'Waiting for Parts', description: 'Awaiting materials' },
  { key: 'timestamp_repair_done', label: 'Repair Completed', description: 'Repair finished' },
  { key: 'timestamp_recommission', label: 'Recommissioning', description: 'Service restored' },
  { key: 'timestamp_recorded', label: 'Recorded', description: 'Logged in GIS' },
  { key: 'timestamp_done', label: 'Done', description: 'Ticket closed' },
];

function formatDateTime(dateString: string | null | undefined): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function calculateDurationMinutes(from: string | null, to: string | null): number | null {
  if (!from || !to) return null;
  const diff = new Date(to).getTime() - new Date(from).getTime();
  return Math.round(diff / (1000 * 60));
}

function formatDuration(minutes: number | null): string {
  if (minutes === null) return '';

  // Less than 60 minutes - show minutes only
  if (minutes < 60) {
    return `${minutes}min`;
  }

  // Less than 1440 minutes (24 hours) - show hours and minutes
  if (minutes < 1440) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins === 0 ? `${hours}h` : `${hours}h ${mins}min`;
  }

  // 24 hours or more - show only days (simplified for averages)
  const days = Math.round(minutes / 1440);
  return `${days}d`;
}

export function OperationalTimesCard({ request }: OperationalTimesCardProps) {
  // Collect only completed timestamps
  const completedStages = TIMELINE_STAGES.filter(stage => {
    const timestamp = request[stage.key];
    return timestamp !== null && timestamp !== undefined;
  });

  // Calculate key durations
  const responseTime = calculateDurationMinutes(request.createdAt, request.timestamp_on_site);
  const leakRunningTime = calculateDurationMinutes(request.createdAt, request.timestamp_decommission);
  const supplyInterruptionTime = calculateDurationMinutes(
    request.timestamp_decommission,
    request.timestamp_recommission
  );
  const repairDuration = calculateDurationMinutes(
    request.timestamp_repair_begin,
    request.timestamp_repair_done
  );
  const totalProcessingTime = calculateDurationMinutes(request.createdAt, request.timestamp_done);

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
      {/* Header */}
      <div className="flex items-center gap-2 mb-6">
        <Clock size={20} className="text-blue-600" />
        <h2 className="text-lg font-semibold text-gray-900">Operational Timeline</h2>
      </div>

      {/* No timestamps message */}
      {completedStages.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No operational stages completed yet</p>
        </div>
      ) : (
        <>
          {/* Timeline */}
          <div className="space-y-0 mb-8">
            {completedStages.map((stage, index) => {
              const timestamp = request[stage.key] as string;
              const isLast = index === completedStages.length - 1;

              return (
                <div key={stage.key} className="flex gap-4">
                  {/* Timeline line and dot */}
                  <div className="flex flex-col items-center">
                    {/* Dot */}
                    <div className="w-3 h-3 rounded-full bg-blue-600 mt-2" />
                    {/* Vertical line */}
                    {!isLast && <div className="w-0.5 h-12 bg-gray-300 mt-2" />}
                  </div>

                  {/* Content */}
                  <div className="pb-4 flex-1">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900 text-sm">{stage.label}</h3>
                          <p className="text-xs text-gray-500 mt-0.5">{stage.description}</p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 mt-2 font-mono">
                        {formatDateTime(timestamp)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Duration Metrics */}
          {(responseTime !== null || leakRunningTime !== null || supplyInterruptionTime !== null || repairDuration !== null || totalProcessingTime !== null) && (
            <div className="border-t pt-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Duration Metrics</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {responseTime !== null && (
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                    <p className="text-xs text-gray-600 font-medium">Response Time</p>
                    <p className="text-lg font-bold text-blue-600">{formatDuration(responseTime)}</p>
                    <p className="text-xs text-gray-500 mt-1">Reported → On Site</p>
                  </div>
                )}

                {leakRunningTime !== null && (
                  <div className="bg-orange-50 rounded-lg p-3 border border-orange-100">
                    <p className="text-xs text-gray-600 font-medium">Leak Running Time</p>
                    <p className="text-lg font-bold text-orange-600">{formatDuration(leakRunningTime)}</p>
                    <p className="text-xs text-gray-500 mt-1">Reported → Decommissioning</p>
                  </div>
                )}

                {supplyInterruptionTime !== null && (
                  <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                    <p className="text-xs text-gray-600 font-medium">Supply Interruption</p>
                    <p className="text-lg font-bold text-purple-600">{formatDuration(supplyInterruptionTime)}</p>
                    <p className="text-xs text-gray-500 mt-1">Decommissioning → Restore</p>
                  </div>
                )}

                {repairDuration !== null && (
                  <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                    <p className="text-xs text-gray-600 font-medium">Repair Duration</p>
                    <p className="text-lg font-bold text-green-600">{formatDuration(repairDuration)}</p>
                    <p className="text-xs text-gray-500 mt-1">Repair Begin → Complete</p>
                  </div>
                )}

                {totalProcessingTime !== null && (
                  <div className="bg-cyan-50 rounded-lg p-3 border border-cyan-100 sm:col-span-2">
                    <p className="text-xs text-gray-600 font-medium">Total Processing Time</p>
                    <p className="text-lg font-bold text-cyan-600">{formatDuration(totalProcessingTime)}</p>
                    <p className="text-xs text-gray-500 mt-1">Reported → Done</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
